/**
 * Notification Panel Component - Панель уведомлений о событиях VK ORD
 * Автор: MarkApp Team
 * Дата: 21 октября 2025
 * 
 * Отображает уведомления о событиях:
 * - Креативы: принят/отклонен/на модерации
 * - Контрагенты: проверен/не прошел проверку  
 * - Договоры: принят/отклонен/на проверке
 * - Акты: утвержден/отклонен
 * - Статистика: принята/превышен лимит
 */

import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './NotificationPanel.css';

const NotificationPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  // =====================================================
  // ЗАГРУЗКА ДАННЫХ
  // =====================================================

  /**
   * Загрузить список уведомлений
   */
  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications/?limit=20');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Загрузить только счетчик непрочитанных
   */
  const loadUnreadCount = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Ошибка загрузки счетчика:', error);
    }
  };

  // =====================================================
  // ДЕЙСТВИЯ С УВЕДОМЛЕНИЯМИ
  // =====================================================

  /**
   * Отметить уведомление как прочитанное
   */
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      loadNotifications();
    } catch (error) {
      console.error('Ошибка отметки:', error);
    }
  };

  /**
   * Отметить все уведомления как прочитанные
   */
  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      loadNotifications();
    } catch (error) {
      console.error('Ошибка:', error);
    }
  };

  /**
   * Архивировать (удалить) уведомление
   */
  const archiveNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      loadNotifications();
    } catch (error) {
      console.error('Ошибка архивирования:', error);
    }
  };

  // =====================================================
  // ЭФФЕКТЫ
  // =====================================================

  /**
   * Загрузка счетчика при монтировании и автообновление
   */
  useEffect(() => {
    loadUnreadCount();
    
    // Обновление счетчика каждые 30 секунд
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Загрузка уведомлений при открытии панели
   */
  useEffect(() => {
    if (showPanel) {
      loadNotifications();
    }
  }, [showPanel]);

  /**
   * Закрытие панели при клике вне неё
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowPanel(false);
      }
    };

    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPanel]);

  // =====================================================
  // ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
  // =====================================================

  /**
   * Получить иконку для типа уведомления
   */
  const getNotificationIcon = (notification) => {
    const icons = {
      'creative': '🎨',
      'contract': '📄',
      'counterparty': '👥',
      'act': '📋',
      'statistics': '📊'
    };
    return icons[notification.type] || '📢';
  };

  /**
   * Форматировать время "5 мин назад"
   */
  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'только что';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} мин назад`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} ч назад`;
    if (diff < 172800000) return 'вчера';
    return date.toLocaleDateString('ru-RU', { 
      day: 'numeric', 
      month: 'short' 
    });
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="notification-container" ref={panelRef}>
      {/* Кнопка колокольчик */}
      <button 
        className="notification-bell"
        onClick={() => setShowPanel(!showPanel)}
        title="Уведомления"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C11.172 2 10.5 2.672 10.5 3.5V4.194C8.013 4.862 6 7.083 6 9.5V14.5L4 16.5V18H20V16.5L18 14.5V9.5C18 7.083 15.987 4.862 13.5 4.194V3.5C13.5 2.672 12.828 2 12 2ZM10 19C10 20.105 10.895 21 12 21C13.105 21 14 20.105 14 19H10Z" fill="currentColor"/>
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>

      {/* Выпадающая панель */}
      {showPanel && (
        <div className="notification-panel">
          {/* Заголовок */}
          <div className="notification-header">
            <h3>Уведомления</h3>
            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="mark-all-read">
                Отметить все
              </button>
            )}
          </div>

          {/* Список уведомлений */}
          <div className="notification-list">
            {loading ? (
              // Загрузка
              <div className="notification-loading">
                <div className="spinner"></div>
                <p>Загрузка...</p>
              </div>
            ) : notifications.length === 0 ? (
              // Пусто
              <div className="notification-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C11.172 2 10.5 2.672 10.5 3.5V4.194C8.013 4.862 6 7.083 6 9.5V14.5L4 16.5V18H20V16.5L18 14.5V9.5C18 7.083 15.987 4.862 13.5 4.194V3.5C13.5 2.672 12.828 2 12 2ZM10 19C10 20.105 10.895 21 12 21C13.105 21 14 20.105 14 19H10Z" fill="#ccc"/>
                </svg>
                <p>Нет уведомлений</p>
              </div>
            ) : (
              // Список
              notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification)}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    {notification.message && (
                      <div className="notification-message">{notification.message}</div>
                    )}
                    <div className="notification-time">{formatTime(notification.created_at)}</div>
                  </div>
                  <button 
                    className="notification-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveNotification(notification.id);
                    }}
                    title="Удалить"
                  >
                    ✕
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Футер */}
          {notifications.length > 0 && (
            <div className="notification-footer">
              <button onClick={() => {
                // TODO: Перейти на страницу всех уведомлений
                
              }}>
                Показать все
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;


